import { showModal, hideModal } from "./utils.js";
import { closeMenu } from "./mobile-menu.js";
import { FORM_SELECTORS, MODAL_CLASSES, FORM_CLASSES } from "./const.js";

/**
 * Класс для управления модальным окном с формой
 * @class FormModal
 *
 * @property {HTMLElement} container - Обёртка формы
 * @property {HTMLElement} form - Элемент формы
 * @property {HTMLElement} overlay - Элемент затемнения
 * @property {HTMLElement} closeButton - Кнопка закрытия модального окна
 * @property {NodeList} triggers - Кнопки с data-modal-target="request-form"
 * @property {boolean} isOpen - Флаг состояния модального окна
 * @property {HTMLElement} responseSuccess - Элемент сообщения об успехе
 * @property {HTMLElement} responseFail - Элемент сообщения об ошибке
 */
export default class FormModal {
  /**
   * Создает экземпляр FormModal
   * @param {HTMLElement} container - Обёртка формы
   * @param {NodeList} triggers - Кнопки с data-modal-target="request-form"
   * @param {HTMLElement} overlay - Элемент затемнения
   * @param {HTMLElement} responseSuccess - Элемент сообщения об успехе
   * @param {HTMLElement} responseFail - Элемент сообщения об ошибке (опционально)
   */
  constructor(container, triggers, overlay, responseSuccess, responseFail) {
    this.container = container;
    this.form = container?.querySelector(FORM_SELECTORS.FORM);
    this.overlay = overlay;
    this.closeButton = this.overlay?.querySelector(
      `.${MODAL_CLASSES.OVERLAY.CLOSE}`,
    );
    this.triggers = triggers;
    this.responseSuccess = responseSuccess;
    this.responseFail = responseFail || null;
    this.isOpen = false;
    this.formValidator = null;
  }

  /**
   * Инициализирует модальное окно
   * @returns {void}
   */
  init() {
    if (!this.form || !this.overlay || !this.triggers) {
      console.error("Form or overlay wasn't found");

      return;
    }

    this.addEventListeners();
  }

  /**
   * Добавляет обработчики событий для открытия и закрытия модального окна
   * @returns {void}
   */
  addEventListeners() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => this.showForm(event));
    });

    this.initCloseHandlers();
  }

  /**
   * Инициализирует обработчики закрытия (overlay, close icon, escape, form close icon)
   * @returns {void}
   */
  initCloseHandlers() {
    this.overlay.addEventListener("click", (event) => {
      if (event.target === this.overlay) {
        this.handleClose();
      }
    });

    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.handleClose());
    }

    const formCloseIcon = this.container?.querySelector(
      ".request-form__close-icon",
    );
    if (formCloseIcon) {
      formCloseIcon.addEventListener("click", () => this.handleClose());
    }

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.isOpen) {
        this.handleClose();
      }
    });
  }

  /**
   * Открывает модальное окно
   * @param {Event} event - Событие клика
   * @returns {void}
   */
  showForm(event) {
    event.preventDefault();

    closeMenu();
    showModal(this.overlay, this.container, this.closeButton);
    this.isOpen = true;
  }

  /**
   * Закрывает модальное окно
   * @returns {void}
   */
  hideForm() {
    hideModal(this.overlay, this.container, this.closeButton);
    this.isOpen = false;

    if (this.formValidator) {
      this.formValidator.enableSubmitButton();
    }
  }

  /**
   * Показывает только контейнер формы (overlay уже видим при переключении языка)
   * @returns {void}
   */
  showFormOnly() {
    if (this.container) {
      this.container.classList.add(MODAL_CLASSES.FORM.ACTIVE);
    }
    this.isOpen = true;
  }

  /**
   * Скрывает только контейнер формы (overlay остаётся видимым)
   * @returns {void}
   */
  hideFormOnly() {
    if (this.container) {
      this.container.classList.remove(MODAL_CLASSES.FORM.ACTIVE);
    }
    this.isOpen = false;
  }

  /**
   * Проверяет, видимо ли любое из response окон
   * @returns {boolean}
   */
  isResponseVisible() {
    if (!this.responseSuccess) {
      return false;
    }

    let isVisible = this.responseSuccess.classList.contains(FORM_CLASSES.RESPONSE_ACTIVE);

    if (this.responseFail) {
      isVisible = isVisible || this.responseFail.classList.contains(FORM_CLASSES.RESPONSE_ACTIVE);
    }

    return isVisible;
  }

  /**
   * Скрывает все response окна
   * @returns {void}
   */
  hideAllResponses() {
    if (this.responseSuccess) {
      this.responseSuccess.classList.remove(FORM_CLASSES.RESPONSE_ACTIVE);
    }

    if (this.responseFail) {
      this.responseFail.classList.remove(FORM_CLASSES.RESPONSE_ACTIVE);
    }

    // Сбросить форму когда закрыли response
    if (this.form) {
      this.form.classList.remove(MODAL_CLASSES.FORM.ACTIVE);
    }
  }

  /**
   * Централизованный обработчик закрытия
   * @returns {void}
   */
  handleClose() {
    if (!this.isOpen) {
      return;
    }

    if (this.isResponseVisible()) {
      this.hideAllResponses();
    }

    this.hideForm();
  }

  /**
   * Устанавливает ссылку на FormValidator
   * @param {FormValidator} formValidator
   * @returns {void}
   */
  setFormValidator(formValidator) {
    this.formValidator = formValidator;
  }
}
